import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import PieChartComponent from "../Graphs/PieChartComponent";

// Utility function to generate light colors consistently
const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    // Use deterministic calculation based on index to ensure consistency
    const hue = (i * 137.5) % 360; // Golden angle approximation for better distribution
    const saturation = 70 + Math.round((i % 3) * 10); // 70-90%
    const lightness = 80 - Math.round((i % 2) * 10); // 70-80%
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  return colors;
};

const CountrySessions = ({ analyticsData, themeBg }) => {
  const fullData = analyticsData?.sessions?.byCountry || [];

  // Process data to show top 6 and group the rest as "Others"
  const processData = (data) => {
    if (!data || data.length === 0) return [];

    // If 6 or fewer items, return them all
    if (data.length <= 6) return data;

    // Get the top 6 items
    const topItems = data.slice(0, 6);

    // Calculate totals for "Others"
    const otherSessions = data
      .slice(6)
      .reduce((sum, item) => sum + (parseInt(item.sessions) || 0), 0);
    const totalSessions = data.reduce(
      (sum, item) => sum + (parseInt(item.sessions) || 0),
      0
    );
    const otherPercentage =
      totalSessions > 0
        ? ((otherSessions / totalSessions) * 100).toFixed(1) + "%"
        : "0%";

    // Add "Others" item if there are remaining countries
    if (otherSessions > 0) {
      topItems.push({
        country: "Others",
        sessions: otherSessions,
        percentage: otherPercentage,
      });
    }

    return topItems;
  };

  // Process data for display
  const displayData = processData(fullData);
  const remainingCount = fullData.length > 6 ? fullData.length - 6 : 0;

  // Generate colors for chart and table - using useMemo to prevent regenerating on every render
  const colors = useMemo(
    () => generateColors(displayData.length),
    [displayData.length]
  );

  return (
    <div>
      <Typography
        variant="h6"
        style={{
          fontWeight: "bold",
          marginBottom: "10px",
          textAlign: "center",
          paddingTop: "20px",
        }}
      >
        Geographic Distribution of Sessions{" "}
      </Typography>
      <div
        style={{
          display: "flex",
          gap: "20px",
          borderRadius: "12px",
          padding: "20px",
          color: "#333",
          backgroundColor: themeBg || "#F3F4F6",
        }}
      >
        {/* Left Side: Pie Chart - passing colors to ensure consistency */}
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <PieChartComponent
            data={displayData.map((item) => ({
              device: item.country,
              sessions: parseInt(item.sessions) || 0,
            }))}
            title="Country Sessions Distribution"
            customColors={colors}
          />
        </div>

        {/* Right Side: Country Data with color indicators */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "bold",
              borderBottom: "2px solid #ccc",
              paddingBottom: "10px",
            }}
          >
            <Typography style={{ flex: 4, fontSize: "14px" }}>
              Country
            </Typography>
            <Typography
              style={{ flex: 2, fontSize: "14px", textAlign: "center" }}
            >
              Sessions ({analyticsData?.sessions?.total || ""})
            </Typography>
            <Typography
              style={{ flex: 1, fontSize: "14px", textAlign: "right" }}
            >
              %
            </Typography>
          </div>

          {/* Display the top 6 countries plus "Others" row if applicable */}
          {displayData.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "5px 0",
                borderBottom: "1px solid #eee",
                backgroundColor:
                  item.country === "Others" ? "#f8f8f8" : "transparent",
                fontWeight: item.country === "Others" ? "bold" : "normal",
              }}
            >
              <div
                style={{
                  flex: 4,
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {/* Color indicator square */}
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: colors[index],
                    borderRadius: "3px",
                    flexShrink: 0,
                  }}
                />

                <Typography
                  style={{
                    fontStyle: item.country === "Others" ? "italic" : "normal",
                  }}
                >
                  {item.country}
                </Typography>
              </div>
              <Typography
                style={{
                  flex: 2,
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {item.sessions}
              </Typography>
              <Typography
                style={{
                  flex: 1,
                  fontSize: "14px",
                  textAlign: "right",
                }}
              >
                {item.percentage}
              </Typography>
            </div>
          ))}

          {/* Show explanatory note if we grouped countries */}
          {remainingCount > 0 && (
            <div
              style={{
                marginTop: "10px",
                fontSize: "12px",
                color: "#666",
                textAlign: "right",
                fontStyle: "italic",
              }}
            >
              * "Others" includes {remainingCount} more{" "}
              {remainingCount === 1 ? "country" : "countries"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountrySessions;
